'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import {
  TRACKED_EVENT_POSITION,
  getBlogSearchPath,
  type BlogCategory,
} from '@/constants';
import { useRouter } from '@/navigation';
import { useKeyboardFocusRing } from '@/hooks/useKeyboardFocusRing.hook';
import CategorySelect from './category-select';
import SearchIcon from './search-icon';

/**
 * 검색이 놓이는 화면 — 헤더 폭이 달라 시안의 데스크톱 입력 폭이 갈린다.
 * 폭 값은 이 컴포넌트가 소유하고, 호출부는 "어디에 쓰는지"만 알려준다.
 * 모바일은 둘 다 남는 폭을 채우므로 이 값이 쓰이지 않는다.
 *
 * `list` 는 시리즈 목록 전용이 아니라 목록형 화면 공통이다 — 검색 결과·카테고리 목록 등이
 * 같은 헤더 폭을 쓴다.
 */
type SearchContext = 'landing' | 'list';

const DESKTOP_WIDTH_CLASS: Record<SearchContext, string> = {
  landing: 'xl:w-[360px]',
  list: 'xl:w-[338px]',
};

/**
 * 카테고리 칩이 달린 블로그 검색 입력.
 *
 * 칩은 **검색 범위 선택 전용**이다 — 칩만 바꿔도 이동하지 않고, 이동은 제출로만 일어나며
 * 목적지는 언제나 검색 결과 페이지다.
 */
export default function BlogSearch({
  context = 'landing',
  defaultCategory = 'all',
  defaultQuery = '',
}: {
  context?: SearchContext;
  /** 이미 그 카테고리를 보고 있는 화면(카테고리 페이지)의 초기 칩 값. 나머지는 '전체' */
  defaultCategory?: BlogCategory;
  /** 검색 결과 페이지에서 방금 검색한 검색어를 입력에 남겨 두기 위한 초기값 */
  defaultQuery?: string;
}) {
  const t = useTranslations('Blog.landing');
  const router = useRouter();
  const [query, setQuery] = useState(defaultQuery);
  const [category, setCategory] = useState<BlogCategory>(defaultCategory);
  const { visible: showFocusRing, focusRingProps } = useKeyboardFocusRing();
  const placeholder = t('searchPlaceholder');

  // 뒤로/앞으로 가기 같은 소프트 내비게이션은 이 컴포넌트를 리마운트하지 않고 default*
  // prop 만 갱신한다 — 동기화하지 않으면 입력·칩이 이전 URL 의 값으로 남는다.
  // 입력 중에는 prop 이 안 바뀌므로 타이핑을 덮어쓰지 않는다
  useEffect(() => setQuery(defaultQuery), [defaultQuery]);
  useEffect(() => setCategory(defaultCategory), [defaultCategory]);

  // `<form>` 을 유지하고 `onSubmit` 만 가로챈다 — Enter 제출을 브라우저 기본 동작으로
  // 두기 위해서다(div 로 바꾸면 Enter 가 죽는다)
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = query.trim();
    // 빈 검색어로는 이동하지 않는다 — 칩만 바꿔도 아무 일이 없다(칩은 검색 범위 선택 전용)
    if (!trimmed) return;

    router.push(getBlogSearchPath({ query: trimmed, category }));
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex w-full items-center gap-3 md:gap-3.5 xl:w-auto"
    >
      {/* 칩과 입력이 한 pill 안에 든다 — 테두리·그림자가 input 이 아니라 이 래퍼에 있다.
          링은 키보드 포커스에서만 그린다(`useKeyboardFocusRing` — 클릭·탭에는 안 뜬다).
          칩은 자기 링을 offset 없이 따로 갖는다 */}
      <div
        {...focusRingProps}
        className={`flex h-10 w-full min-w-0 items-center gap-1.5 rounded-full border border-solid border-3b-text-hint bg-3b-background-surface pl-2 pr-3 shadow-[0px_5px_4px_-4px_rgba(0,0,0,0.02),0px_1px_2px_0px_rgba(0,0,0,0.06)] md:h-12 ${showFocusRing ? 'focus-ring-always' : ''} ${DESKTOP_WIDTH_CLASS[context]}`}
      >
        <CategorySelect value={category} onChange={setCategory} />
        {/* `type="search"` 는 유지한다 — 모바일 키보드에 검색 키가 뜨고 시맨틱도 맞다.
            대신 딸려 오는 네이티브 clear(✕) 버튼은 시안에 없어 숨긴다(WebKit 전용 장식) */}
        <input
          type="search"
          name="q"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="h-full w-full min-w-0 text-ellipsis bg-transparent text-body2 font-normal text-3b-text-primary placeholder:text-3b-text-hint focus:outline-none md:text-[16px] md:leading-26 md:tracking-[-0.176px] [&::-webkit-search-cancel-button]:appearance-none"
        />
      </div>
      {/* 아이콘만 있는 버튼이라 접근 이름을 aria-label 로 준다. 40×40(모바일)·48×48(데스크톱)
          모두 WCAG 2.2 타깃 크기(24×24) 이상이다 */}
      <TrackedButton
        label="blog_search_submit"
        position={TRACKED_EVENT_POSITION.MAIN_HEADER}
        type="submit"
        aria-label={t('searchSubmit')}
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-3b-blue-light-900 text-3b-text-contrast transition-colors hover:bg-3b-blue-light-700 md:size-12"
      >
        <SearchIcon />
      </TrackedButton>
    </form>
  );
}
